import { useEffect, useMemo, useState } from 'react';
import { useForm, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRightIcon, CheckIcon } from '@phosphor-icons/react';
import { Button, Modal, Spinner } from '@skupreme/shared/ui';
import { allMarketplaceRegionDropdownHook } from '@hooks/marketplace-region';
import { ErrorMessageConstant } from '@utility/constants/common';
import FpPartnersService from '@utility/services/fp-partners';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import { IntegrationType } from '../../lib/integration';
import { isZipValid, toCountryOptions, type Region } from '../country-options';
import {
  AddProviderStep,
  addProviderSchema,
  credentialsPayload,
  extensivPayload,
  isOneStepIntegration,
  type AddProviderValues,
} from '../../lib/add-provider';
import { ContactStep } from './ContactStep';
import { CredentialsStep } from './CredentialsStep';
import { CustomWmsForm } from './CustomWmsForm';
import { ExtensivStep } from './ExtensivStep';
import { LocationStep } from './LocationStep';
import { useWmsWarehouses } from '../../hooks/use-provider-tabs';
import { ProviderTypeStep, type WmsWarehouse } from './ProviderTypeStep';

type ApiResult = { formValues?: { _id?: string }; data?: { _id?: string } };

type ExistingProvider = { _id: string; integration_type?: string };

type AddProviderModalProps = {
  open: boolean;
  /**
   * Created: the new provider's id. Type changed: `true`. Cancelled: nothing.
   * A custom WMS hands back whatever its connect call returned first.
   */
  closeModal: (result?: unknown) => void;
  /** Set to change this provider's type instead of creating one. */
  existingProvider?: ExistingProvider | null;
};

/* Each one-step integration connects through its own endpoint. */
const CONNECT: Record<
  string,
  (payload: Record<string, unknown>) => Promise<ApiResult>
> = {
  [IntegrationType.tactical_logistics]: payload =>
    StorageProviderService.authFulfillmentTactical(payload),
  [IntegrationType.mintsoft]: payload =>
    StorageProviderService.authMintsoft(payload),
  [IntegrationType.skudrop]: payload =>
    StorageProviderService.authSkudrop(payload),
  [IntegrationType.santaFe]: payload =>
    StorageProviderService.authSantFe(payload),
  [IntegrationType.camelot]: payload =>
    StorageProviderService.authCamelot(payload),
  [IntegrationType.goodCang]: payload =>
    StorageProviderService.authGoodCang(payload),
  [IntegrationType.shipHero]: ({ code, quantity_type }) =>
    StorageProviderService.authShipHero(code, { quantity_type }),
};

const createdId = (result: ApiResult | undefined) =>
  result?.formValues?._id || result?.data?._id;

export function AddProviderModal({
  open,
  closeModal,
  existingProvider = null,
}: AddProviderModalProps) {
  const changeTypeOnly = !!existingProvider;
  const [step, setStep] = useState<AddProviderStep>(AddProviderStep.INITIAL);
  const [dialCodeLength, setDialCodeLength] = useState(1);
  const [saving, setSaving] = useState(false);

  const { data: regionData, loading: regionLoading } =
    allMarketplaceRegionDropdownHook();
  const { data: wmsData } = useWmsWarehouses();
  const regions = regionData as Region[];
  const warehouses = (wmsData ?? []) as WmsWarehouse[];

  const {
    control,
    formState: { isValid, dirtyFields, errors },
    trigger,
    watch,
    setValue,
  } = useForm<AddProviderValues>({
    resolver: zodResolver(
      addProviderSchema(step, { changeTypeOnly, dialCodeLength }),
    ),
    defaultValues: { is_fulfillable: false, is_warehouse: false },
    mode: 'all',
    /* A field that is not drawn leaves the payload, as it did before. */
    shouldUnregister: true,
  });

  useEffect(() => {
    setStep(AddProviderStep.INITIAL);
    setValue('quantity_type', 'unit');
  }, [open, setValue]);

  useEffect(() => {
    trigger();
  }, [step, trigger]);

  const values = watch();
  const type = values.integration_type ?? '';
  const isDeliverr = type === IntegrationType.deliverr;
  const isExtensiv = type === IntegrationType['3pl'];
  const customWms = warehouses.find(warehouse => warehouse._id === type);
  const isWmsOneStep =
    !!customWms &&
    !Object.keys(customWms.integrationMetadata?.customFields ?? {}).length;
  const isOneStep = isOneStepIntegration(type) || !!customWms;
  const isLocation = step === AddProviderStep.LOCATION;

  const fieldError = (name: FieldPath<AddProviderValues>) =>
    (dirtyFields as Record<string, unknown>)[name] && errors[name]?.message
      ? String(errors[name]?.message)
      : '';

  const isZipCodeValid = useMemo(
    () => isZipValid(regions, values.country, values.zip_code),
    [regions, values.country, values.zip_code],
  );

  const countryOptions = useMemo(() => toCountryOptions(regions), [regions]);

  const run = async (action: () => Promise<void>) => {
    try {
      setSaving(true);
      await action();
    } catch (error) {
      ToastMessage.error(
        (error as { error?: string })?.error ||
          ErrorMessageConstant.defaultAPIError,
      );
    } finally {
      setSaving(false);
    }
  };

  const created = (result: ApiResult) => {
    ToastMessage.success('Storage Provider added successfully.');
    closeModal(createdId(result));
  };

  const changeType = async (payload: Record<string, unknown>) => {
    await StorageProviderService.updateProviderType(existingProvider?._id, {
      ...payload,
      integration_type: type,
    });
    closeModal(true);
  };

  const connectOneStep = () =>
    run(async () => {
      const payload = credentialsPayload(values);
      if (changeTypeOnly) {
        await changeType(payload);
      } else {
        created(await CONNECT[type](payload));
      }
    });

  const createProvider = () =>
    run(async () => created(await StorageProviderService.add(values)));

  const saveExtensiv = () =>
    run(async () => {
      const payload = extensivPayload(values);
      if (changeTypeOnly) {
        await changeType({
          ...payload.api_key,
          quantity_type: values.quantity_type,
        });
      } else {
        created(await StorageProviderService.add(payload));
      }
    });

  const connectDeliverr = () =>
    run(async () => {
      const result = await StorageProviderService.authFulfillmentDeliverr();
      let url: string | undefined = result?.data;
      if (changeTypeOnly && url) {
        url = `${url}&state=${existingProvider?._id}`;
      }
      if (url) {
        window.location.href = url;
      }
    });

  const connectWms = async (warehouse: WmsWarehouse) => {
    try {
      setSaving(true);
      const result = await FpPartnersService.integrateWMSwarehouse({
        wms_organization_id: warehouse._id,
      });
      ToastMessage.success(
        `${warehouse.integrationMetadata?.displayName || warehouse.name} Connected Successfully`,
      );
      closeModal(result?.providers?.[0]);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSaving(false);
    }
  };

  const onNext = async () => {
    if (isWmsOneStep && customWms) {
      await connectWms(customWms);
      return;
    }

    switch (step) {
      case AddProviderStep.INITIAL:
        if (isDeliverr) {
          await connectDeliverr();
        } else if (isExtensiv && changeTypeOnly) {
          setStep(AddProviderStep.AUTHENTICATION);
        } else {
          setStep(AddProviderStep.LOCATION);
        }
        break;
      case AddProviderStep.LOCATION:
        if (isOneStepIntegration(type)) {
          await connectOneStep();
        } else if (isExtensiv || values.is_fulfillable || values.is_warehouse) {
          setValue('contact_email', values.email);
          setStep(AddProviderStep.CONTACT);
        }
        break;
      case AddProviderStep.CONTACT:
        if (isExtensiv) {
          setStep(AddProviderStep.AUTHENTICATION);
        } else {
          await createProvider();
        }
        break;
      default:
        await saveExtensiv();
        break;
    }
  };

  const onBack = () => {
    switch (step) {
      case AddProviderStep.INITIAL:
        closeModal();
        break;
      case AddProviderStep.LOCATION:
        setStep(AddProviderStep.INITIAL);
        break;
      case AddProviderStep.CONTACT:
        setStep(
          changeTypeOnly ? AddProviderStep.INITIAL : AddProviderStep.LOCATION,
        );
        break;
      default:
        setStep(
          changeTypeOnly ? AddProviderStep.INITIAL : AddProviderStep.CONTACT,
        );
        break;
    }
  };

  const isCreate =
    isWmsOneStep ||
    (isOneStep
      ? isLocation
      : isExtensiv
        ? step === AddProviderStep.AUTHENTICATION
        : step === AddProviderStep.CONTACT);

  const title = changeTypeOnly
    ? 'Change Storage Provider Type'
    : {
        [AddProviderStep.INITIAL]: 'Create Storage Provider',
        [AddProviderStep.LOCATION]: isOneStep
          ? 'Create Storage Provider'
          : 'Create Storage Provider - Location',
        [AddProviderStep.CONTACT]: 'Create Storage Provider - Contact',
        [AddProviderStep.AUTHENTICATION]: 'Create Storage Provider - Extensiv',
      }[step];

  const isLocationFields = isLocation && !isOneStep;
  /* A custom WMS's fields are their own form with their own Create. */
  const isCustomWmsFields = isLocation && !!customWms;
  const countryFlag = regions.find(
    region => region.id === values.country,
  )?.flag;

  const footer = isDeliverr ? null : (
    <>
      <Button onPress={onBack}>
        {step === AddProviderStep.INITIAL ? 'Cancel' : 'Back'}
      </Button>
      {isCustomWmsFields ? null : (
        <Button
          variant="primary"
          isDisabled={
            !isValid || (isLocationFields && !isZipCodeValid) || saving
          }
          onPress={onNext}
        >
          {saving ? <Spinner size="sm" /> : null}
          {isCreate ? (
            <>
              <CheckIcon size={12} /> Create
            </>
          ) : (
            <>
              Next <ArrowRightIcon size={12} />
            </>
          )}
        </Button>
      )}
    </>
  );

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && closeModal()}
      size="lg"
      showCloseButton
      title={title}
      footer={footer}
    >
      <form
        autoComplete="off"
        onSubmit={event => event.preventDefault()}
        className="sk:flex sk:flex-col sk:gap-4"
      >
        <ProviderTypeStep
          control={control}
          fieldError={fieldError}
          isVisible={step === AddProviderStep.INITIAL}
          showName={!(isDeliverr || isOneStep) && !changeTypeOnly}
          changeTypeOnly={changeTypeOnly}
          currentIntegrationType={existingProvider?.integration_type}
          wmsWarehouses={warehouses}
        />
        <LocationStep
          control={control}
          fieldError={fieldError}
          isVisible={isLocationFields}
          countryOptions={regionLoading ? [] : countryOptions}
          isZipCodeValid={isZipCodeValid}
          hasZipAndCountry={!!values.country && !!values.zip_code}
          isExtensiv={isExtensiv}
          needsProviderKind={
            isValid && !(values.is_fulfillable || values.is_warehouse)
          }
        />
        {isCustomWmsFields && customWms ? (
          <CustomWmsForm warehouse={customWms} onConnected={closeModal} />
        ) : (
          <CredentialsStep
            control={control}
            fieldError={fieldError}
            isVisible={isLocation && isOneStep}
            integrationType={type}
          />
        )}
        <ContactStep
          control={control}
          fieldError={fieldError}
          isVisible={step === AddProviderStep.CONTACT}
          countryFlag={countryFlag}
          isExtensiv={isExtensiv}
          onDialCodeLengthChange={setDialCodeLength}
        />
        <ExtensivStep
          control={control}
          fieldError={fieldError}
          isVisible={step === AddProviderStep.AUTHENTICATION}
        />
        {isDeliverr ? (
          <Button variant="primary" onPress={onNext} isDisabled={saving}>
            {saving ? <Spinner size="sm" /> : null}
            <img
              src="/images/marketplaces/deliverr.svg"
              alt=""
              height={18}
              width={18}
            />
            Connect Deliverr, Inc.
          </Button>
        ) : null}
      </form>
    </Modal>
  );
}
